import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FiCheck, FiEdit2, FiRefreshCw, FiSave, FiTrash2, FiX, FiPlus, FiUpload } from "react-icons/fi";
import {
  activateAdNetwork,
  addAdNetworkKey,
  createAdKey,
  createAdNetwork,
  createAdsSetting,
  deleteAdKey,
  deleteAdNetwork,
  getAdConfig,
  getAdKeys,
  getAdNetwork,
  getAdNetworks,
  getAdsSetting,
  getPublicAdSetting,
  importAdNetworkGoogleAds,
  normalizeGoogleAdsFromImport,
  parseImportJsonText,
  updateAdKey,
  updateAdNetwork,
  updateAdsSetting,
  extractPlacementKeysPayload,
  isMissingAdsSettingMessage,
  collectKeyNamesFromGoogleAds,
  ensurePlacementKeysForGoogleAds,
  canonicalPlacementKeyName,
  remapGoogleAdsKeyNames,
  filterGoogleAdsToKeys,
  getGoogleAdsKeyRenameNotes,
  isValidPlacementKeyName,
} from "../../api/adKeysApi";

const ADS_TYPE_OPTIONS = [
  { value: 1, label: "Facebook" },
  { value: 2, label: "Google" },
  { value: 3, label: "Fallback" },
];

const ADS_TYPE_COLOR = {
  1: "bg-[#ffffe0] text-[#999900]",
  2: "bg-red-50 text-red-500",
  3: "bg-amber-50 text-amber-600",
};

const keyNameOf = (row) => row.keyName || row.key || row.name || "";

const validateKeyNameInput = (name) => {
  const trimmed = canonicalPlacementKeyName(name);
  if (!isValidPlacementKeyName(trimmed)) {
    toast.error("Key must start with a letter and use only lowercase letters, numbers, and underscores");
    return null;
  }
  return trimmed;
};

const androidOf = (row, googleAds) => {
  const name = keyNameOf(row);
  return row.android ?? row.units?.android ?? googleAds?.android?.[name] ?? "";
};

const iosOf = (row, googleAds) => {
  const name = keyNameOf(row);
  return row.ios ?? row.units?.ios ?? googleAds?.ios?.[name] ?? "";
};

const normalizeBool = (v) => v === true || v === 1 || v === "1";

const buildUnitForm = (list, googleAds) => {
  const form = {};
  list.forEach((row) => {
    const name = keyNameOf(row);
    if (!name) return;
    form[name] = {
      android: androidOf(row, googleAds),
      ios: iosOf(row, googleAds),
    };
  });
  return form;
};

const inferKeyCategory = (name) => {
  if (name === "app_open" || name.startsWith("app_open")) return "app_open";
  if (name === "banner" || name.startsWith("banner_")) return "banner";
  if (name === "native" || name.startsWith("native_") || name.startsWith("native")) return "native";
  if (name === "interstitial" || name.startsWith("interstitial_")) return "interstitial";
  if (name === "reward" || name.startsWith("reward_")) return "reward";
  return "other";
};

const mergeKeyRowsWithGoogleAds = (rows, googleAds) => {
  const byName = new Map();
  (rows || []).forEach((row) => {
    const name = keyNameOf(row);
    if (name) byName.set(name, row);
  });
  collectKeyNamesFromGoogleAds(googleAds).forEach((name) => {
    if (!byName.has(name)) {
      byName.set(name, {
        id: `import-${name}`,
        keyName: name,
        key: name,
        name,
        category: inferKeyCategory(name),
      });
    }
  });
  return [...byName.values()].sort((a, b) => keyNameOf(a).localeCompare(keyNameOf(b)));
};

const resolveCategories = (categories, list) => {
  if (Array.isArray(categories) && categories.length > 0) return categories;
  const grouped = {};
  list.forEach((row) => {
    const cat = String(row.category || "other").toLowerCase();
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(row);
  });
  return Object.entries(grouped).map(([category, keys]) => ({
    category,
    label: category.replace(/_/g, " ").toUpperCase(),
    count: keys.length,
    keys,
  }));
};

/* ─── Section A: Ads platform ─── */
const AdsPlatformSection = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [settingId, setSettingId] = useState(null);
  const [form, setForm] = useState({ isAdsRun: true, adsType: 2 });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getAdsSetting();
      const data = result.data;
      const row = Array.isArray(data) ? data[0] : data;
      if (row && row.id != null) {
        setSettingId(row.id);
        setForm({
          isAdsRun: normalizeBool(row.isAdsRun),
          adsType: Number(row.adsType) || 2,
        });
      } else {
        setSettingId(null);
        setForm({ isAdsRun: true, adsType: 2 });
      }
    } catch (err) {
      if (err.status === 404 || isMissingAdsSettingMessage(err.message)) {
        setSettingId(null);
        setForm({ isAdsRun: true, adsType: 2 });
        setError("");
      } else {
        setError(err.message || "Failed to load ads platform settings");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { isAdsRun: form.isAdsRun, adsType: form.adsType };
      if (settingId != null) {
        await updateAdsSetting(settingId, body);
        toast.success("Ads platform settings updated");
      } else {
        const result = await createAdsSetting(body);
        const created = result.data;
        const id = created?.id ?? (Array.isArray(created) ? created[0]?.id : null);
        if (id != null) setSettingId(id);
        toast.success("Ads platform settings created");
      }
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to save ads platform settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700">Ads platform</h3>
        <p className="text-xs text-gray-400 mt-0.5">Enable ads and choose the primary network</p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-10">
          <div className="w-6 h-6 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="px-5 py-5 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-sm">{error}</div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                ADS_TYPE_COLOR[form.adsType] || "bg-gray-100 text-gray-500"
              }`}
            >
              {ADS_TYPE_OPTIONS.find((o) => o.value === form.adsType)?.label || `Type ${form.adsType}`}
            </span>
            {settingId == null && (
              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                Not saved yet — save to create configuration
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-xl">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Ads type</label>
              <select
                value={form.adsType}
                onChange={(e) => setForm((f) => ({ ...f, adsType: Number(e.target.value) }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] bg-white"
              >
                {ADS_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between sm:justify-start sm:gap-6 pt-1">
              <label className="text-xs font-medium text-gray-500">Ads running</label>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium ${form.isAdsRun ? "text-green-500" : "text-gray-400"}`}>
                  {form.isAdsRun ? "Enabled" : "Disabled"}
                </span>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, isAdsRun: !f.isAdsRun }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    form.isAdsRun ? "bg-[#ffff00]" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      form.isAdsRun ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium px-4 py-2.5 rounded-lg transition"
          >
            <FiSave size={14} />
            {saving ? "Saving..." : "Save platform settings"}
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── Section B: Ad placement keys ─── */
const AdPlacementKeysSection = ({ onKeysChanged, refreshToken = 0 }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [keys, setKeys] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [showAddKeyInput, setShowAddKeyInput] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getAdKeys();
      const parsed = extractPlacementKeysPayload(result);
      setKeys(parsed.keys);
      setCategories(parsed.categories);
      onKeysChanged?.(parsed.keys, parsed.googleAds);
    } catch (err) {
      setError(err.message || "Failed to load ad placement keys");
    } finally {
      setLoading(false);
    }
  }, [onKeysChanged]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const categoryCards = useMemo(() => resolveCategories(categories, keys), [categories, keys]);

  const handleAddKey = async (e) => {
    e.preventDefault();
    const trimmed = validateKeyNameInput(newKeyName);
    if (!trimmed) return;
    setAdding(true);
    try {
      const result = await createAdKey(trimmed);
      toast.success(`Key "${trimmed}" added`);
      setNewKeyName("");
      setShowAddKeyInput(false);
      const parsed = extractPlacementKeysPayload(result);
      if (parsed.keys.length > 0) {
        setKeys(parsed.keys);
        setCategories(parsed.categories);
        onKeysChanged?.(parsed.keys, parsed.googleAds);
      }
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to add key");
    } finally {
      setAdding(false);
    }
  };

  const startRename = (row) => {
    setEditingId(row.id);
    setEditName(keyNameOf(row));
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditName("");
  };

  const commitRename = async (row) => {
    const trimmed = validateKeyNameInput(editName);
    if (!trimmed) return;
    if (trimmed === keyNameOf(row)) {
      cancelRename();
      return;
    }
    setSavingRename(true);
    try {
      await updateAdKey(row.id, trimmed);
      toast.success("Key renamed");
      cancelRename();
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to rename key");
    } finally {
      setSavingRename(false);
    }
  };

  const handleDelete = async (row) => {
    const name = keyNameOf(row);
    if (!window.confirm(`Delete placement key "${name}"? This cannot be undone without an app update.`)) return;
    setDeletingId(row.id);
    try {
      await deleteAdKey(row.id);
      toast.success("Key deleted");
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to delete key");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700">Ad placement keys</h3>
        <p className="text-xs text-gray-500 mt-1 max-w-3xl leading-relaxed">
          The keys the app receives in <code className="text-[11px] bg-gray-100 px-1 rounded">googleAds.android</code> /{" "}
          <code className="text-[11px] bg-gray-100 px-1 rounded">googleAds.ios</code>. Every network has every key — a new
          key is sent at once as &quot;&quot; until you fill unit IDs below.
        </p>
      </div>

      <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
        {showAddKeyInput ? (
          <form onSubmit={handleAddKey} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="banner_profile"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              autoFocus
              className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] font-mono"
            />
            <button
              type="submit"
              disabled={adding}
              className="flex items-center justify-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium px-4 py-2.5 rounded-lg transition whitespace-nowrap"
            >
              <FiCheck size={14} />
              {adding ? "Adding..." : "Save key"}
            </button>
            <button
              type="button"
              onClick={() => {
                setNewKeyName("");
                setShowAddKeyInput(false);
              }}
              className="flex items-center justify-center gap-2 text-gray-600 hover:bg-gray-100 text-sm font-medium px-4 py-2.5 rounded-lg border border-gray-200 transition whitespace-nowrap"
            >
              <FiX size={14} />
              Cancel
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setShowAddKeyInput(true)}
            className="flex items-center justify-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] text-black text-sm font-medium px-4 py-2.5 rounded-lg transition"
          >
            <FiPlus size={14} />
            Add Key
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-10">
          <div className="w-6 h-6 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="m-5 bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-sm">{error}</div>
      ) : keys.length === 0 ? (
        <div className="py-10 text-center text-gray-400 text-sm">No placement keys yet. Add one above.</div>
      ) : (
        <div className="p-5 space-y-4">
          {categoryCards.map((card) => (
            <div key={card.category} className="border border-gray-100 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600 tracking-wide">{card.label || card.category}</span>
                <span className="text-[10px] font-medium text-gray-400 bg-white border border-gray-100 px-2 py-0.5 rounded-full">
                  {card.count ?? (card.keys?.length ?? 0)}
                </span>
              </div>
              <ul className="divide-y divide-gray-50">
                {(card.keys || []).map((row) => (
                  <li key={row.id} className="flex items-center gap-2 px-4 py-3 hover:bg-gray-50/80 transition">
                    {editingId === row.id ? (
                      <>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                          autoFocus
                        />
                        <button
                          type="button"
                          disabled={savingRename}
                          onClick={() => commitRename(row)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Save name"
                        >
                          <FiCheck size={16} />
                        </button>
                        <button type="button" onClick={cancelRename} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg">
                          <FiX size={16} />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm font-mono text-gray-800 truncate">{keyNameOf(row)}</span>
                        <button
                          type="button"
                          onClick={() => startRename(row)}
                          className="flex items-center gap-1 text-xs text-amber-500 hover:bg-amber-50 px-2 py-1.5 rounded-lg transition"
                        >
                          <FiEdit2 size={13} /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(row)}
                          disabled={deletingId === row.id}
                          className="flex items-center gap-1 text-xs text-red-500 hover:bg-red-50 px-2 py-1.5 rounded-lg transition disabled:opacity-60"
                        >
                          <FiTrash2 size={13} /> Delete
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <p className="px-5 py-4 border-t border-gray-100 text-xs text-gray-400 bg-gray-50/30">
        The app reads keys by name — rename or delete one only together with an app update.
      </p>
    </div>
  );
};

const extractNetworkName = (payload, fallback = "") => {
  if (!payload || typeof payload !== "object") return fallback;
  const root = payload.data ?? payload;
  if (root && typeof root === "object" && !Array.isArray(root)) {
    if (root.name != null && String(root.name).trim() !== "") return String(root.name);
    if (root.network?.name) return String(root.network.name);
    if (root.networkName) return String(root.networkName);
  }
  if (payload.name != null && String(payload.name).trim() !== "") return String(payload.name);
  if (payload.networkName) return String(payload.networkName);
  return fallback;
};

const applyNetworkPayload = (payload, setNetworkName, setCategories, setKeys, setUnitForm, fallbackName = "") => {
  const name = extractNetworkName(payload, fallbackName);
  if (name) setNetworkName(name);
  const parsed = extractPlacementKeysPayload(payload);
  setKeys(parsed.keys);
  setCategories(parsed.categories);
  setUnitForm(buildUnitForm(parsed.keys, parsed.googleAds));
};

/* ─── Network edit modal ─── */
const NetworkEditModal = ({ networkId, isActive, initialName = "", onClose, onSaved, onRegistryChanged }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [networkName, setNetworkName] = useState(initialName);
  const [keys, setKeys] = useState([]);
  const [categories, setCategories] = useState([]);
  const [unitForm, setUnitForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [showAddKeyInput, setShowAddKeyInput] = useState(false);
  const [addingKey, setAddingKey] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [importing, setImporting] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getAdNetwork(networkId);
      applyNetworkPayload(result, setNetworkName, setCategories, setKeys, setUnitForm, initialName);
    } catch (err) {
      setError(err.message || "Failed to load network");
    } finally {
      setLoading(false);
    }
  }, [networkId, initialName]);

  useEffect(() => {
    setNetworkName(initialName);
  }, [networkId, initialName]);

  useEffect(() => {
    load();
  }, [load]);

  const categoryCards = useMemo(() => resolveCategories(categories, keys), [categories, keys]);

  const setUnit = (name, platform, value) => {
    setUnitForm((prev) => ({
      ...prev,
      [name]: { ...prev[name], [platform]: value },
    }));
  };

  const handleAddKey = async (e) => {
    e.preventDefault();
    const trimmed = validateKeyNameInput(newKeyName);
    if (!trimmed) return;
    setAddingKey(true);
    try {
      const result = await addAdNetworkKey(networkId, trimmed);
      toast.success(`Key "${trimmed}" added`);
      setNewKeyName("");
      setShowAddKeyInput(false);
      applyNetworkPayload(result, setNetworkName, setCategories, setKeys, setUnitForm);
      onSaved?.();
    } catch (err) {
      toast.error(err.message || "Failed to add key");
    } finally {
      setAddingKey(false);
    }
  };

  const handleImport = async () => {
    let parsed;
    try {
      parsed = parseImportJsonText(importJson);
    } catch (err) {
      toast.error(err.message === "Paste JSON first" ? err.message : "Invalid JSON — check commas and quotes");
      return;
    }
    let googleAds = normalizeGoogleAdsFromImport(parsed);
    const renameNotes = getGoogleAdsKeyRenameNotes(googleAds);
    googleAds = remapGoogleAdsKeyNames(googleAds);
    const hasUnits =
      googleAds &&
      (Object.keys(googleAds.android || {}).length > 0 || Object.keys(googleAds.ios || {}).length > 0);
    if (!hasUnits) {
      toast.error(
        'Paste JSON with "googleAds.android" / "googleAds.ios", or the full GET /api/v1/adsetting response'
      );
      return;
    }
    setImporting(true);
    try {
      if (renameNotes.length > 0) {
        toast(`Normalized keys: ${renameNotes.join(", ")}`, { icon: "ℹ️", duration: 6000 });
      }
      const existingNames = keys.map((row) => canonicalPlacementKeyName(keyNameOf(row))).filter(Boolean);
      const { created, failed, allNames } = await ensurePlacementKeysForGoogleAds(
        networkId,
        googleAds,
        existingNames
      );
      googleAds = filterGoogleAdsToKeys(googleAds, allNames);
      if (created.length > 0) {
        toast.success(`Added ${created.length} placement key(s) from JSON`);
      }
      if (failed.length > 0) {
        toast.error(
          `Some keys could not be created: ${failed.slice(0, 5).map((f) => f.keyName).join(", ")}${
            failed.length > 5 ? "…" : ""
          }`
        );
      }

      await importAdNetworkGoogleAds(networkId, googleAds);
      const fresh = await getAdNetwork(networkId);
      const payload = extractPlacementKeysPayload(fresh);
      const mergedGoogleAds = {
        android: { ...payload.googleAds?.android, ...googleAds.android },
        ios: { ...payload.googleAds?.ios, ...googleAds.ios },
      };
      const list = mergeKeyRowsWithGoogleAds(
        payload.keys.length > 0 ? payload.keys : keys,
        mergedGoogleAds
      );
      const nameAfterImport = extractNetworkName(fresh, networkName || initialName);
      if (nameAfterImport) setNetworkName(nameAfterImport);
      setKeys(list);
      setCategories(payload.categories.length > 0 ? payload.categories : resolveCategories([], list));
      setUnitForm(buildUnitForm(list, mergedGoogleAds));
      toast.success("JSON imported — unit IDs applied");
      setImportJson("");
      setShowImport(false);
      onSaved?.();
      onRegistryChanged?.();
    } catch (err) {
      toast.error(err.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const android = {};
      const ios = {};
      keys.forEach((row) => {
        const raw = keyNameOf(row);
        const name = canonicalPlacementKeyName(raw);
        if (!name || !isValidPlacementKeyName(name)) return;
        const unit = unitForm[raw] ?? unitForm[name];
        android[name] = unit?.android ?? "";
        ios[name] = unit?.ios ?? "";
      });
      await updateAdNetwork(networkId, {
        name: networkName.trim(),
        googleAds: { android, ios },
      });
      toast.success("Network saved");
      if (isActive) toast.success("Active network — app API will use these unit IDs");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to save network");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-3 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-gray-800">
              Edit network{networkName ? `: ${networkName}` : initialName ? `: ${initialName}` : ""}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Unit IDs per placement key{isActive ? " · this network is active for the app" : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <FiX size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-6 h-6 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-sm">{error}</div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Network name</label>
                <input
                  type="text"
                  value={networkName}
                  onChange={(e) => setNetworkName(e.target.value)}
                  className="w-full max-w-md px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                />
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                {showAddKeyInput ? (
                  <form onSubmit={handleAddKey} className="flex flex-1 flex-col sm:flex-row gap-3 min-w-[200px]">
                    <input
                      type="text"
                      placeholder="banner_profile"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      autoFocus
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                    />
                    <button
                      type="submit"
                      disabled={addingKey}
                      className="flex items-center justify-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium px-4 py-2.5 rounded-lg transition whitespace-nowrap"
                    >
                      <FiCheck size={14} />
                      {addingKey ? "Adding..." : "Save key"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setNewKeyName("");
                        setShowAddKeyInput(false);
                      }}
                      className="flex items-center justify-center gap-2 text-gray-600 hover:bg-gray-100 text-sm font-medium px-4 py-2.5 rounded-lg border border-gray-200 transition whitespace-nowrap"
                    >
                      <FiX size={14} />
                      Cancel
                    </button>
                  </form>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowAddKeyInput(true)}
                    className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-medium px-4 py-2.5 rounded-lg transition whitespace-nowrap"
                  >
                    <FiPlus size={14} />
                    Add key
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowImport((v) => !v)}
                  className="flex items-center justify-center gap-2 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2.5 rounded-lg transition whitespace-nowrap"
                >
                  <FiUpload size={14} />
                  Import JSON
                </button>
              </div>

              {showImport && (
                <div className="border border-gray-100 rounded-xl p-4 bg-gray-50/50 space-y-3">
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Accepted:{" "}
                    <code className="bg-white px-1 rounded text-[10px]">
                      {"{ \"googleAds\": { \"android\": {...}, \"ios\": {...} } }"}
                    </code>
                    , platform-only{" "}
                    <code className="bg-white px-1 rounded text-[10px]">{"{ \"android\": {...}, \"ios\": {...} }"}</code>
                    , or full{" "}
                    <code className="bg-white px-1 rounded text-[10px]">GET /api/v1/adsetting</code> response.
                  </p>
                  <textarea
                    value={importJson}
                    onChange={(e) => setImportJson(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                    placeholder='{"googleAds":{"android":{},"ios":{}}}'
                  />
                  <button
                    type="button"
                    onClick={handleImport}
                    disabled={importing}
                    className="bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium px-4 py-2 rounded-lg"
                  >
                    {importing ? "Importing..." : "Apply import"}
                  </button>
                </div>
              )}

              {keys.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No placement keys. Add keys here or in the section above.</p>
              ) : (
                categoryCards.map((card) => (
                  <div key={card.category} className="border border-gray-100 rounded-xl overflow-hidden">
                    <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-600 tracking-wide">{card.label || card.category}</span>
                      <span className="text-[10px] text-gray-400">{card.count ?? card.keys?.length ?? 0} keys</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[10px] text-gray-400 border-b border-gray-50">
                            <th className="px-4 py-2 font-medium">Key</th>
                            <th className="px-4 py-2 font-medium">Android</th>
                            <th className="px-4 py-2 font-medium">iOS</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {(card.keys || []).map((row) => {
                            const name = keyNameOf(row);
                            return (
                              <tr key={row.id ?? name}>
                                <td className="px-4 py-2 font-mono text-xs text-gray-700 whitespace-nowrap">{name}</td>
                                <td className="px-4 py-2">
                                  <input
                                    type="text"
                                    placeholder="ca-app-pub-…/…"
                                    value={unitForm[name]?.android ?? ""}
                                    onChange={(e) => setUnit(name, "android", e.target.value)}
                                    className="w-full min-w-[160px] px-2 py-1.5 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <input
                                    type="text"
                                    placeholder="ca-app-pub-…/…"
                                    value={unitForm[name]?.ios ?? ""}
                                    onChange={(e) => setUnit(name, "ios", e.target.value)}
                                    className="w-full min-w-[160px] px-2 py-1.5 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 shrink-0 bg-white">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading || !!error}
            className="flex-1 flex items-center justify-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium py-2.5 rounded-lg transition"
          >
            <FiSave size={14} />
            {saving ? "Saving..." : "Save network"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Section C: Ad networks ─── */
const AdNetworksSection = ({ refreshToken, onPlacementKeysRefresh }) => {
  const [networks, setNetworks] = useState([]);
  const [activeNetworkId, setActiveNetworkId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newNetworkName, setNewNetworkName] = useState("");
  const [createError, setCreateError] = useState("");
  const [creating, setCreating] = useState(false);
  const [activatingId, setActivatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editingNetwork, setEditingNetwork] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getAdNetworks();
      const list =
        result.networks ??
        result.data?.networks ??
        (Array.isArray(result.data) ? result.data : []);
      setNetworks(Array.isArray(list) ? list : []);
      setActiveNetworkId(result.activeNetworkId ?? result.data?.activeNetworkId ?? null);
    } catch (err) {
      setError(err.message || "Failed to load ad networks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const name = newNetworkName.trim();
    if (!name) {
      setCreateError("Enter a network name (e.g. Jungle, GameAdZone).");
      toast.error("Enter a network name first");
      return;
    }
    setCreateError("");
    setCreating(true);
    try {
      const result = await createAdNetwork({ name });
      const created =
        result.network ?? result.data?.network ?? (result.data?.id ? result.data : null);
      if (created?.id) {
        setNetworks((prev) => {
          if (prev.some((n) => n.id === created.id)) return prev;
          return [...prev, { ...created, isActive: !!created.isActive }];
        });
      }
      toast.success(`Network "${name}" created`);
      setNewNetworkName("");
      await load();
    } catch (err) {
      const msg = err.message || "Failed to create network";
      setCreateError(msg);
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleActivate = async (network) => {
    setActivatingId(network.id);
    try {
      await activateAdNetwork(network.id);
      toast.success(`"${network.name}" is now the active network`);
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to activate network");
    } finally {
      setActivatingId(null);
    }
  };

  const handleDelete = async (network) => {
    if (network.isActive) {
      toast.error("Cannot delete the active network. Activate another network first.");
      return;
    }
    if (!window.confirm(`Delete network "${network.name}"?`)) return;
    setDeletingId(network.id);
    try {
      await deleteAdNetwork(network.id);
      toast.success("Network deleted");
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to delete network");
    } finally {
      setDeletingId(null);
    }
  };

  const loadPublicPreview = async () => {
    setLoadingPreview(true);
    try {
      const result = await getPublicAdSetting();
      setPreview(result.data ?? result);
      toast.success("Loaded public app API (GET /api/v1/adsetting)");
    } catch (err) {
      toast.error(err.message || "Failed to load public ad setting");
    } finally {
      setLoadingPreview(false);
    }
  };

  const loadAdminPreview = async () => {
    setLoadingPreview(true);
    try {
      const result = await getAdConfig();
      setPreview(result.data ?? result);
      toast.success("Loaded full ad config preview");
    } catch (err) {
      toast.error(err.message || "Failed to load ad config");
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Google Ads networks</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl leading-relaxed">
              Each network stores its own unit IDs per placement key. Only one network is active — the app receives that
              network&apos;s <code className="text-[11px] bg-gray-100 px-1 rounded">googleAds</code> on{" "}
              <code className="text-[11px] bg-gray-100 px-1 rounded">GET /api/v1/adsetting</code>.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadPublicPreview}
              disabled={loadingPreview}
              className="flex items-center gap-1.5 text-xs text-gray-600 hover:bg-gray-100 px-3 py-2 rounded-lg border border-gray-200 transition"
            >
              <FiRefreshCw size={13} className={loadingPreview ? "animate-spin" : ""} />
              App API preview
            </button>
            <button
              type="button"
              onClick={loadAdminPreview}
              disabled={loadingPreview}
              className="flex items-center gap-1.5 text-xs text-gray-600 hover:bg-gray-100 px-3 py-2 rounded-lg border border-gray-200 transition"
            >
              Full config
            </button>
          </div>
        </div>

        <form onSubmit={handleCreate} className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 space-y-2">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Network name (e.g. Jungle, GameAdZone)"
              value={newNetworkName}
              onChange={(e) => {
                setNewNetworkName(e.target.value);
                if (createError) setCreateError("");
              }}
              required
              minLength={1}
              className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
            />
            <button
              type="submit"
              disabled={creating}
              className="flex items-center justify-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium px-4 py-2.5 rounded-lg transition whitespace-nowrap"
            >
              <FiPlus size={14} />
              {creating ? "Creating..." : "Create network"}
            </button>
          </div>
          {createError && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{createError}</p>
          )}
        </form>

        {loading ? (
          <div className="flex justify-center items-center py-10">
            <div className="w-6 h-6 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="m-5 bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-sm">{error}</div>
        ) : networks.length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">No networks yet. Create one above (e.g. Google AdMob).</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {networks.map((net) => (
              <div key={net.id} className="flex flex-wrap items-center gap-3 px-5 py-4 hover:bg-gray-50/80 transition">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-800">{net.name}</span>
                    {net.isActive && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide bg-[#ffffcc] text-[#999900] px-2 py-0.5 rounded-full">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono">{net.slug}</p>
                </div>
                <div className="text-xs text-gray-500">
                  Units:{" "}
                  <span className="font-medium text-gray-700">
                    {net.unitsSet ?? "—"} / {net.unitsTotal ?? "—"}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!net.isActive && (
                    <button
                      type="button"
                      disabled={activatingId === net.id}
                      onClick={() => handleActivate(net)}
                      className="text-xs font-medium text-[#999900] hover:bg-[#ffffe0] px-3 py-1.5 rounded-lg border border-[#ffff99] transition disabled:opacity-60"
                    >
                      {activatingId === net.id ? "Activating..." : "Set active"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditingNetwork(net)}
                    className="flex items-center gap-1 text-xs text-amber-500 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg transition"
                  >
                    <FiEdit2 size={13} /> Edit IDs
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(net)}
                    disabled={deletingId === net.id || net.isActive}
                    className="flex items-center gap-1 text-xs text-red-500 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
                    title={net.isActive ? "Deactivate another network first" : "Delete network"}
                  >
                    <FiTrash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {preview && (
          <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
            <p className="text-xs font-medium text-gray-500 mb-2">Preview</p>
            <pre className="text-[11px] text-gray-700 bg-white border border-gray-100 rounded-lg p-3 overflow-x-auto max-h-64">
              {JSON.stringify(preview, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {editingNetwork && (
        <NetworkEditModal
          networkId={editingNetwork.id}
          initialName={editingNetwork.name ?? ""}
          isActive={!!editingNetwork.isActive}
          onClose={() => setEditingNetwork(null)}
          onSaved={load}
          onRegistryChanged={onPlacementKeysRefresh}
        />
      )}
    </>
  );
};

const AdsSettings = () => {
  const [networksRefreshToken, setNetworksRefreshToken] = useState(0);
  const [placementKeysRefreshToken, setPlacementKeysRefreshToken] = useState(0);

  const bumpNetworksRefresh = useCallback(() => {
    setNetworksRefreshToken((t) => t + 1);
  }, []);

  const bumpPlacementKeysRefresh = useCallback(() => {
    setPlacementKeysRefreshToken((t) => t + 1);
  }, []);

  return (
    <div className="space-y-5">
      <AdsPlatformSection />
      <AdNetworksSection
        refreshToken={networksRefreshToken}
        onPlacementKeysRefresh={bumpPlacementKeysRefresh}
      />
      <AdPlacementKeysSection
        onKeysChanged={bumpNetworksRefresh}
        refreshToken={placementKeysRefreshToken}
      />
    </div>
  );
};

export default AdsSettings;
